import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { filter, map, mergeMap, zip } from 'rxjs';
import { ApiRequestType } from 'src/app/shared/enums/api.enums';
import { ERROR_CODE_MERCHANT_FAILED_TO_LOAD } from 'src/app/shared/enums/error-code.enums';
import { apiResponse, getApiRequest, staticApiResponseAction } from '../api/api.actions';
import { Response } from '../api/api.interface';
import { responseRequestType } from '../api/api.utilities';
import { updateClientConfiguration } from '../client-configuration/client-configuration.actions';
import { environmentLoadedSuccess } from '../environment/environment.actions';
import { setGatewaySessionTokenAction } from '../gateway-session-token/gateway-session-token.actions';
import { initializeFailedAction } from '../initialize/initialize.actions';
import { staticDataLoadedFailureAction, staticDataLoadedSuccessAction } from './static-data.actions';
import { StaticDataResponse } from './static-data.model';

@Injectable()
export class StaticDataEffects {
  /**
   * Fetches static data
   */
  loadStaticData$ = createEffect(() =>
    zip(
      this.actions$.pipe(
        ofType(updateClientConfiguration),
        filter(({ payload }) => payload.found)
      ),
      this.actions$.pipe(ofType(environmentLoadedSuccess))
    ).pipe(
      map(([{ payload }, _]) =>
        getApiRequest({
          requestType: ApiRequestType.GET_STATIC_DATA,
          params: { tenantName: payload.tenantName, merchantId: payload.merchantId, language: payload.language },
        })
      )
    )
  );

  loadStaticDataSuccessResponse$ = createEffect(() =>
    this.actions$.pipe(
      ofType(apiResponse),
      filter(responseRequestType(ApiRequestType.GET_STATIC_DATA)),
      filter(({ isOk }: any) => isOk),
      map(({ response }: Response<StaticDataResponse>) => staticDataLoadedSuccessAction({ payload: response.data }))
    )
  );

  loadStaticDataFailureResponse$ = createEffect(() =>
    this.actions$.pipe(
      ofType(apiResponse),
      filter(responseRequestType(ApiRequestType.GET_STATIC_DATA)),
      filter(({ isOk }: any) => !isOk),
      map(() => staticDataLoadedFailureAction())
    )
  );

  /**
   * Injects static data into the store
   */
  injectStaticData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(staticDataLoadedSuccessAction),
      mergeMap(({ payload }) => {
        const { GetApplicationConfigs, GetApplicationLocale, GetPaymentConfiguration, SessionToken } = payload;
        return [
          staticApiResponseAction({
            status: GetPaymentConfiguration.status,
            isOk: !!GetPaymentConfiguration.response?.length,
            requestType: ApiRequestType.GET_PAYMENT_CONFIGURATION,
            response: GetPaymentConfiguration.response,
          }),
          staticApiResponseAction({
            status: GetApplicationConfigs.status,
            isOk: !!GetApplicationConfigs.response?.length,
            requestType: ApiRequestType.GET_APPLICATION_CONFIG,
            response: GetApplicationConfigs.response,
          }),
          staticApiResponseAction({
            status: GetApplicationLocale.status,
            isOk: !!GetApplicationLocale.response?.length,
            requestType: ApiRequestType.GET_APPLICATION_LOCALE,
            response: GetApplicationLocale.response,
          }),
          setGatewaySessionTokenAction({ token: SessionToken }),
        ];
      })
    )
  );

  /**
   * Sends initialization failure on static data failure
   */
  staticDataFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(staticDataLoadedFailureAction),
      map(() =>
        initializeFailedAction({
          errorCode: ERROR_CODE_MERCHANT_FAILED_TO_LOAD,
          reason: `Merchant doesn't exist or failed to load`,
        })
      )
    )
  );

  /**
   * @param actions$ Our stream of actions.
   */
  constructor(private actions$: Actions) {}
}
